/**
 * Expense List Component
 * 
 * Displays list of expenses with participant shares and balances.
 * Following Tabby design principles: clean table layout, minimal styling.
 */

import React from 'react';
import { Expense, Participant } from '../features/expenseSplitter/groupModel';
import { formatAmount } from '../features/expenseSplitter/expenseService';

interface ExpenseListProps {
  expenses: Expense[];
  participants: Participant[];
  balances: Record<string, number>;
  currency: string;
}

const ExpenseList: React.FC<ExpenseListProps> = ({
  expenses,
  participants,
  balances,
  currency
}) => {
  const getParticipantName = (participantId: string): string => {
    return participants.find(p => p.id === participantId)?.name || 'Unknown';
  };

  const getParticipantNamesInExpense = (expense: Expense): string => {
    const names = expense.participantShares
      .filter(share => share.isIncluded)
      .map(share => getParticipantName(share.participantId));
    
    return names.join(', ');
  };

  if (expenses.length === 0) {
    return (
      <div style={{ 
        padding: '40px', 
        textAlign: 'center', 
        color: 'var(--color-gray-600)',
        border: '2px dashed var(--color-gray-200)',
        borderRadius: 'var(--radius-lg)'
      }}>
        <p>No expenses yet</p>
        <p style={{ fontSize: '14px', marginTop: '8px' }}>
          Add your first expense to get started
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Expenses List */}
      <div style={{ marginBottom: '32px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={{
                textAlign: 'left',
                fontSize: '11px',
                fontWeight: 'var(--font-weight-medium)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--color-gray-400)',
                padding: '12px 0',
                borderBottom: '1px solid var(--color-gray-100)'
              }}>
                Description
              </th>
              <th style={{
                textAlign: 'right',
                fontSize: '11px',
                fontWeight: 'var(--font-weight-medium)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--color-gray-400)',
                padding: '12px 0',
                borderBottom: '1px solid var(--color-gray-100)'
              }}>
                Amount
              </th>
              <th style={{
                textAlign: 'left',
                fontSize: '11px',
                fontWeight: 'var(--font-weight-medium)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--color-gray-400)',
                padding: '12px 0',
                borderBottom: '1px solid var(--color-gray-100)'
              }}>
                Paid by
              </th>
              <th style={{
                textAlign: 'left',
                fontSize: '11px',
                fontWeight: 'var(--font-weight-medium)',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: 'var(--color-gray-400)',
                padding: '12px 0',
                borderBottom: '1px solid var(--color-gray-100)'
              }}>
                Split between
              </th>
            </tr>
          </thead>
          <tbody>
            {expenses.map((expense) => (
              <tr key={expense.id}>
                <td style={{
                  padding: '16px 0',
                  borderTop: '1px solid var(--color-gray-100)',
                  fontSize: '14px'
                }}>
                  <div>
                    <strong>{expense.description}</strong>
                    {expense.notes && (
                      <div style={{
                        fontSize: '13px',
                        color: 'var(--color-gray-600)',
                        marginTop: '2px'
                      }}>
                        {expense.notes}
                      </div>
                    )}
                  </div>
                </td>
                <td style={{
                  padding: '16px 0',
                  borderTop: '1px solid var(--color-gray-100)',
                  fontSize: '14px',
                  textAlign: 'right',
                  fontWeight: 'var(--font-weight-medium)'
                }}>
                  {formatAmount(expense.amount, expense.currency)}
                </td>
                <td style={{
                  padding: '16px 0',
                  borderTop: '1px solid var(--color-gray-100)',
                  fontSize: '14px'
                }}>
                  {getParticipantName(expense.paidBy)}
                </td>
                <td style={{
                  padding: '16px 0',
                  borderTop: '1px solid var(--color-gray-100)',
                  fontSize: '13px',
                  color: 'var(--color-gray-600)'
                }}>
                  {getParticipantNamesInExpense(expense)}
                  <div style={{ fontSize: '12px', marginTop: '2px' }}>
                    {expense.splitMode}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mixed Currency Warning */}
      {(() => {
        const currencies = [...new Set(expenses.map(e => e.currency || currency))];
        const hasMixedCurrencies = currencies.length > 1;
        
        if (hasMixedCurrencies) {
          return (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#fef3c7',
              border: '1px solid #fbbf24',
              borderRadius: 'var(--radius-base)',
              fontSize: '13px',
              color: '#92400e'
            }}>
              ⚠️ <strong>Mixed Currencies:</strong> This group has expenses in multiple currencies ({currencies.join(', ')}). 
              Balances shown below only include expenses in {currency}.
            </div>
          );
        }
        return null;
      })()}

      {/* Balances Summary */}
      <div style={{
        padding: '20px',
        backgroundColor: 'var(--color-gray-50)',
        borderRadius: 'var(--radius-lg)'
      }}>
        <h4 style={{ marginBottom: '16px' }}>Current Balances ({currency})</h4>
        <div style={{ display: 'grid', gap: '12px' }}>
          {participants.map(participant => {
            const balance = balances[participant.id] || 0;
            const isOwed = balance > 0;
            const owes = balance < 0;
            
            return (
              <div
                key={participant.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '12px 16px',
                  backgroundColor: 'white',
                  borderRadius: 'var(--radius-base)',
                  fontSize: '14px'
                }}
              >
                <span style={{ fontWeight: 'var(--font-weight-medium)' }}>
                  {participant.name}
                </span>
                <span style={{
                  color: isOwed 
                    ? 'var(--color-success)' 
                    : owes 
                      ? 'var(--color-error)' 
                      : 'var(--color-gray-600)',
                  fontWeight: 'var(--font-weight-medium)'
                }}>
                  {balance === 0 
                    ? 'Settled' 
                    : `${isOwed ? '+' : ''}${formatAmount(balance, currency)}`
                  }
                </span>
              </div>
            );
          })}
        </div>
        
        {/* Quick Summary */}
        <div style={{ 
          marginTop: '16px', 
          padding: '12px', 
          fontSize: '13px', 
          color: 'var(--color-gray-600)',
          textAlign: 'center'
        }}>
          {(() => {
            // Group expenses by currency
            const expensesByCurrency = expenses.reduce((acc, expense) => {
              const curr = expense.currency || currency;
              acc[curr] = (acc[curr] || 0) + expense.amount;
              return acc;
            }, {} as Record<string, number>);

            const totals = Object.entries(expensesByCurrency)
              .map(([curr, amount]) => formatAmount(amount, curr))
              .join(', ');

            return `Total expenses: ${totals}`;
          })()}
        </div>
      </div>
    </div>
  );
};

export default ExpenseList;
